import materialKeys from '@/api/queries/materials/keyFactory';
import projectKeys from '@/api/queries/project/keyFactory';
import EntityTagControl from '@/ui/components/entityTagControl/EntityTagControl';
import SidebarSplit from '@/ui/components/sidebarSplit/SidebarSplit';
import {
  Badge,
  Button,
  Card,
  CardButton,
  ContextMenu,
  Sidebar,
  SidebarProps,
  SkeletonLoader,
} from '@payaca/ambrosia';
import CurrencyValue from '@payaca/components/currencyValue/CurrencyValue';
import List from '@payaca/components/list/List';

import { MaterialsPermissions } from '@payaca/permissions/materials/materials.permissions';
import { userHasRequiredPermission } from '@payaca/permissions/permissions.utils';
import { useDeal } from '@payaca/store/hooks/appState';
import { TaggableEntityType } from '@payaca/types/tagTypes';
import UntitledIcon from '@payaca/untitled-icons';
import { clstx } from '@payaca/utilities/cvaMerge';
import { useQueryClient } from '@tanstack/react-query';
import { FC, PropsWithChildren, useMemo, useState } from 'react';
import useGetMaterial from '../../../../api/queries/materials/useGetMaterial';
import { useSelector } from '../../../../api/state';
import { getUserRoles } from '../../../../utils/stateAccessors';
import CreateEditSupplierMaterialModal, {
  CreateEditSupplierMaterial,
} from '../../../components/createEditSupplierMaterialModal/CreateEditSupplierMaterialModal';
import EditMaterialModal from './EditMaterialModal';

const ViewMaterialSidebarContent: FC<{
  materialId?: string;
  projectId?: number;
}> = (props) => {
  const { materialId, projectId } = props;
  const userRoles = useSelector(getUserRoles);
  const queryClient = useQueryClient();

  const {
    data,
    isFetching,
    refetch: refetchMaterial,
  } = useGetMaterial(materialId);
  const deal = useDeal(projectId);
  const material = data?.material;
  const [showSupplierMaterialModal, setShowSupplierMaterialModal] =
    useState<Partial<CreateEditSupplierMaterial> | null>(null);
  const [showEditMaterialModal, setShowEditMaterialModal] = useState(false);

  const linkedMaterialSuppliers = material?.suppliedBy || [];

  const materialContextMenuOptions = useMemo(() => {
    const options = [];
    if (
      material?.source === 'SELF' &&
      userHasRequiredPermission(userRoles, [
        MaterialsPermissions.PERSIST_MATERIAL,
      ])
    ) {
      options.push({
        label: 'Edit',
        onClick: () => setShowEditMaterialModal(true),
      });
    }
    return options;
  }, [userRoles, material]);

  if (!material && isFetching) {
    return (
      <SidebarSplit
        leftTab={{ label: 'Material' }}
        rightTab={{ label: 'Linked Suppliers' }}
      >
        <div className="flex flex-col gap-4 px-4 py-3.5">
          <SkeletonLoader.Square className="h-[200px] w-[200px] rounded" />
          <div className="flex flex-col gap-2">
            <SkeletonLoader.Text className="w-20" />
            <SkeletonLoader.Title className="w-1/2" />
          </div>
          <div className="flex flex-col gap-2">
            <SkeletonLoader.Text className="w-20" />
            <SkeletonLoader className="h-16 w-full rounded-lg" />
          </div>
          <div className="flex flex-col gap-2">
            <SkeletonLoader.Text className="w-20" />
            <SkeletonLoader.Title className="w-2/5" />
          </div>
        </div>
        <div className="space-y-4 bg-gray-50 p-3.5 md:border-l">
          <SkeletonLoader.SupplierMaterialCard />
          <SkeletonLoader.SupplierMaterialCard />
          <SkeletonLoader.SupplierMaterialCard />
        </div>
      </SidebarSplit>
    );
  }

  return (
    <>
      <SidebarSplit
        leftTab={{ label: 'Material' }}
        rightTab={{
          label: 'Linked Suppliers',
          badge:
            linkedMaterialSuppliers.length > 0 ? (
              <Badge variant="soft" colour="blue">
                {linkedMaterialSuppliers.length}
              </Badge>
            ) : null,
        }}
      >
        <div className="flex flex-col space-y-3.5 overflow-auto px-4 py-3.5">
          <div className="flex flex-row items-start justify-between pb-4">
            <div
              className={clstx(
                'flex h-[200px] w-[200px] basis-[200px] shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white'
              )}
            >
              {material?.image?.thumbnailUrl ? (
                <img src={material.image.thumbnailUrl} alt={material?.name} />
              ) : (
                <UntitledIcon
                  name="image-01.3"
                  className="non-scaling-stroke h-20 w-20"
                />
              )}
            </div>
            <div className="gap-4 flex flex-col items-end">
              {!!materialContextMenuOptions.length && (
                <ContextMenu
                  variant={'white'}
                  size={'sm'}
                  menuItems={materialContextMenuOptions}
                />
              )}
              {material && (
                <EntityTagControl
                  entityId={+material?.internalId}
                  entityType={TaggableEntityType.MATERIAL}
                  entityTags={
                    material?.tags.map((tag) => ({
                      id: +tag.id,
                      tagText: tag.name,
                      tagColour: tag.colour || '',
                      isSystemManaged: false,
                      archivedAt: null,
                    })) || []
                  }
                  onEntityTagsChange={() => {
                    void queryClient.invalidateQueries({
                      queryKey: materialKeys.material(material.id),
                    });

                    if (deal?.publicId) {
                      void queryClient.invalidateQueries({
                        queryKey: projectKeys.materialsList(deal.publicId),
                      });
                    }
                  }}
                />
              )}
            </div>
          </div>

          <View>
            <View.Property>
              <View.Property.Label>Name</View.Property.Label>
              <View.Property.Value>{material?.name}</View.Property.Value>
            </View.Property>
            <View.Property>
              <View.Property.Label>Description</View.Property.Label>
              <View.Property.Value>{material?.description}</View.Property.Value>
            </View.Property>
            <View.Property>
              <View.Property.Label>Category</View.Property.Label>
              <View.Property.Value>
                {material?.category?.name && (
                  <Badge variant="soft" colour="gray">
                    {material?.category?.name}
                  </Badge>
                )}
              </View.Property.Value>
            </View.Property>
          </View>
        </div>

        {/* linked material suppliers */}
        <div className="flex flex-col overflow-auto bg-gray-50 p-3.5 md:border-l">
          <List
            items={linkedMaterialSuppliers}
            prefix={
              <>
                <h4>Linked Suppliers ({linkedMaterialSuppliers.length})</h4>

                <Button
                  className="w-full"
                  variant={'white'}
                  size={'sm'}
                  onClick={() => {
                    if (material?.internalId) {
                      setShowSupplierMaterialModal({
                        materialInternalId: +material.internalId,
                        materialId: material.id,
                      });
                    }
                  }}
                >
                  Link new Supplier
                </Button>
              </>
            }
            item={(supplierMaterial) => {
              const supplierMaterialEl = !!supplierMaterial?.reference && (
                <span className="text-sm">
                  Ref: {supplierMaterial.reference}
                </span>
              );

              return (
                <CardButton
                  onClick={() => {
                    if (material?.internalId) {
                      setShowSupplierMaterialModal({
                        supplierId: supplierMaterial.supplier.id,
                        supplierInternalId: +supplierMaterial.supplier.id,
                        id: supplierMaterial.id,
                        materialInternalId: +material.internalId,
                        materialId: material.id,
                        price: supplierMaterial.price.unitPriceExcTax.value,
                        url: supplierMaterial.url,
                        reference: supplierMaterial.reference,
                        taxRateId: supplierMaterial.price.taxRate?.id,
                        isPreferredSupplier:
                          material?.preferredSupplierId ===
                          supplierMaterial.supplier.id,
                        isSystemManaged: supplierMaterial.isSystemManaged,
                      });
                    }
                  }}
                >
                  <Card.Body className="flex flex-row justify-between">
                    <div className="flex flex-col">
                      <span>{supplierMaterial.supplier.name}</span>
                      {supplierMaterial.url ? (
                        <a
                          href={supplierMaterial.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {supplierMaterialEl}
                          <UntitledIcon
                            name="link-external-01.3"
                            className="ml-2 h-3.5 w-3.5"
                          />
                        </a>
                      ) : (
                        supplierMaterialEl
                      )}
                    </div>
                    <div className="flex flex-col items-end">
                      <CurrencyValue
                        value={supplierMaterial.price.unitPriceExcTax.value}
                        currency={
                          supplierMaterial.price.unitPriceExcTax.currency
                        }
                      />
                      {material?.preferredSupplierId ===
                        supplierMaterial.supplier.id && (
                        <Badge variant="soft" colour="yellow" size="sm">
                          Preferred Supplier
                        </Badge>
                      )}
                    </div>
                  </Card.Body>
                </CardButton>
              );
            }}
          />
        </div>
      </SidebarSplit>
      {material && (
        <EditMaterialModal
          isOpen={showEditMaterialModal}
          onClose={() => setShowEditMaterialModal(false)}
          material={{
            id: material.id,
            internalId: +material.internalId,
            name: material.name,
            description: material?.description || undefined,
            ...(material.image
              ? {
                  image: {
                    id: material.image.id,
                    thumbnailUrl: material.image.thumbnailUrl || '',
                  },
                }
              : {}),
            ...(material?.category?.id
              ? {
                  category: {
                    id: +material.category.id,
                    label: material?.category?.name,
                  },
                }
              : {}),
          }}
          onPersistMaterialSuccess={() => {
            void refetchMaterial();
          }}
        />
      )}
      <CreateEditSupplierMaterialModal
        isOpen={!!showSupplierMaterialModal}
        onClose={() => setShowSupplierMaterialModal(null)}
        supplierMaterial={showSupplierMaterialModal || undefined}
        isMaterialSelectionDisabled={true}
        onPersistSupplierMaterialSuccess={() => {
          setShowSupplierMaterialModal(null);
          void refetchMaterial();
        }}
        isSupplierSelectionDisabled={!!showSupplierMaterialModal?.supplierId}
      />
    </>
  );
};

const ViewForm: FC<PropsWithChildren> = (props) => {
  return (
    <div className="grid grid-cols-[max-content_auto]">{props.children}</div>
  );
};
const ViewFormProperty: FC<PropsWithChildren> = (props) => {
  return <>{props.children}</>;
};
const ViewFormPropertyLabel: FC<PropsWithChildren> = (props) => {
  return (
    <span className="col-auto border-t py-4 pr-4 text-gray-600">
      {props.children}:
    </span>
  );
};
const ViewFormPropertyValue: FC<PropsWithChildren> = (props) => {
  return <span className="border-t py-4">{props.children}</span>;
};
const View = Object.assign(ViewForm, {
  Property: Object.assign(ViewFormProperty, {
    Label: ViewFormPropertyLabel,
    Value: ViewFormPropertyValue,
  }),
});

const ViewMaterialSidebar: FC<
  Omit<SidebarProps, 'size' | 'title' | 'behind'> & {
    materialId?: string;
    projectId?: number;
  }
> = (props) => {
  const { materialId, projectId, isOpen, onClose, ...rest } = props;

  return (
    <>
      <Sidebar
        isOpen={isOpen}
        size="lg"
        title="Material"
        onClose={onClose}
        {...rest}
      >
        <Sidebar.Body>
          <ViewMaterialSidebarContent
            materialId={materialId}
            projectId={projectId}
          />
        </Sidebar.Body>
      </Sidebar>
    </>
  );
};

export default ViewMaterialSidebar;
